import { useEffect, useRef } from 'react';
import { API_BASE } from './api';
import { getSession } from './auth';

export interface SseHint {
  type: 'hint';
  auditEventId: string;
  eventType: string;
}

const lastSeenKey = (orgId: string) => `skillsou.sse.lastSeen.${orgId}`;

export function readLastSeen(orgId: string): string | null {
  try {
    return localStorage.getItem(lastSeenKey(orgId));
  } catch {
    return null;
  }
}

export function writeLastSeen(orgId: string, eventId: string): void {
  try {
    localStorage.setItem(lastSeenKey(orgId), eventId);
  } catch {
    // ignore quota / disabled storage
  }
}

export interface UseOrgSseOptions {
  enabled?: boolean;
  onHint?: (hint: SseHint) => void;
}

/**
 * Subscribes the current tab to /orgs/:id/events. Per-device last-seen lives
 * in localStorage; the hook seeds the cold-load query param from it so a
 * fresh tab catches up on anything that landed while it was closed.
 *
 * Idempotency contract: callers must treat onHint as a refetch trigger only,
 * never as authoritative state. Re-delivery of the same hint is fine because
 * the consumer re-fetches the latest server state on every fire.
 */
export function useOrgSse(orgId: string | null | undefined, options: UseOrgSseOptions = {}): void {
  const { enabled = true, onHint } = options;
  const onHintRef = useRef(onHint);
  useEffect(() => {
    onHintRef.current = onHint;
  }, [onHint]);

  useEffect(() => {
    if (!orgId || !enabled) return;
    if (typeof EventSource === 'undefined') return;

    const seed = readLastSeen(orgId);
    const session = getSession();
    if (!session) return;
    const qs = new URLSearchParams();
    qs.set('bearer', session.token);
    if (seed) qs.set('last_event_id', seed);
    const url = `${API_BASE}/orgs/${encodeURIComponent(orgId)}/events?${qs.toString()}`;
    const es = new EventSource(url, { withCredentials: false });

    const handleHint = (ev: MessageEvent) => {
      try {
        const payload = JSON.parse(ev.data) as SseHint;
        if (payload?.type === 'hint' && payload.auditEventId) {
          writeLastSeen(orgId, payload.auditEventId);
          onHintRef.current?.(payload);
        }
      } catch {
        // malformed event — ignore
      }
    };

    es.addEventListener('hint', handleHint as EventListener);

    return () => {
      es.removeEventListener('hint', handleHint as EventListener);
      es.close();
    };
  }, [orgId, enabled]);
}
